import * as Haptics from "expo-haptics";
import { Calendar, Copy, ExternalLink, MessageCircle, Share2, Users } from "lucide-react-native";
import { useCallback, useEffect, useState } from "react";
import { Pressable, Share, StyleSheet, Text, View } from "react-native";

import { Reveal } from "../reveal";
import { Badge, EmptyState, ErrorState, LoadingState, Panel, Stat } from "../ui";
import { getPublishedCentralMemorialBySlug, type PublicCentralMemorial } from "../../lib/public-central-memorial";
import { formatLongDate, formatTimeRange } from "../../lib/format";
import * as Clipboard from "../../lib/clipboard";
import { palette, radii, spacing } from "../../theme/tokens";

type NormalizedEvent = {
  id: string;
  title: string;
  startAt: string;
  endAt: string;
  venue: string;
  address: string;
  livestreamUrl: string;
};

function normalizeEvent(record: Record<string, string>): NormalizedEvent {
  return {
    address: record.address ?? "",
    endAt: record.endAt ?? "",
    id: record.id ?? "",
    livestreamUrl: record.livestreamUrl ?? "",
    startAt: record.startAt ?? "",
    title: record.title ?? "",
    venue: record.venue ?? "",
  };
}

/**
 * Memorial tab of the family follow-up portal (WP-2.3).
 *
 * Fetches the published memorial by slug and presents it read-only. A
 * published memorial carries funeral-home approval, so the family cannot edit
 * it from this tab — changes go through the Messages tab so the care team
 * can re-approve.
 */
export function FamilyPortalMemorialTab({
  memorialSlug,
}: {
  memorialSlug: string;
}) {
  const [memorial, setMemorial] = useState<PublicCentralMemorial | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);

  const load = useCallback(async () => {
    if (!memorialSlug) {
      setLoading(false);
      setError("This memorial is not available.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const result = await getPublishedCentralMemorialBySlug(memorialSlug);
      setMemorial(result);
    } catch (caught) {
      const message =
        caught instanceof Error ? caught.message : "Could not load the memorial.";
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [memorialSlug]);

  useEffect(() => {
    void load();
  }, [load]);

  if (loading) {
    return (
      <Panel style={styles.placeholderPanel}>
        <LoadingState label="Loading memorial" />
      </Panel>
    );
  }

  if (error) {
    return (
      <Panel style={styles.placeholderPanel}>
        <ErrorState title="Memorial unavailable" description={error} />
      </Panel>
    );
  }

  if (!memorial) {
    return (
      <Panel style={styles.placeholderPanel}>
        <EmptyState
          title="Memorial not available"
          description="This memorial is no longer available. Please contact your care team if you have questions."
        />
      </Panel>
    );
  }

  const expiresAt = memorial.publication.expiresAt;
  const isExpired = expiresAt !== null && Date.parse(expiresAt) < Date.now();

  if (isExpired) {
    return (
      <Panel style={styles.placeholderPanel}>
        <EmptyState
          title="This memorial has expired"
          description="The public memorial page is no longer available. Please contact your care team if you have questions."
        />
      </Panel>
    );
  }

  const events = memorial.serviceEvents.map(normalizeEvent).filter((e) => Boolean(e.id && e.title));
  const shareUrl = memorial.distribution.canonicalUrl;
  const lovedOneName = memorial.lovedOneName;

  async function handleCopyLink() {
    await Clipboard.setStringAsync(shareUrl);
    Haptics.notificationAsync(Haptics.NotificationFeedbackType.Success);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  async function handleShare() {
    try {
      await Share.share({
        message: `You're invited to honor ${lovedOneName}. RSVP and get event details here: ${shareUrl}`,
        url: shareUrl,
        title: `Memorial for ${lovedOneName}`,
      });
    } catch {
      // User cancelled
    }
  }

  return (
    <View style={styles.content}>
      {/* Stats */}
      <Reveal>
        <View style={styles.statRow}>
          <Stat
            value={String(memorial.guestbook.length)}
            label="Guestbook entries"
            accent="cedar"
          />
          <Stat
            value={memorial.rsvpEnabled ? "Open" : "Closed"}
            label={memorial.rsvpEnabled ? "RSVPs are open" : "RSVPs are closed"}
            accent="forest"
          />
        </View>
      </Reveal>

      {/* Share links */}
      <Reveal delay={80} scale>
        <Panel style={styles.sharePanel}>
          <View style={styles.sharePanelHeader}>
            <Share2 color={palette.cedar} size={18} strokeWidth={1.8} />
            <Text style={styles.sharePanelTitle}>Share with Friends & Family</Text>
          </View>

          <View style={styles.linkRow}>
            <View style={styles.linkBox}>
              <Text style={styles.linkLabel}>Memorial page</Text>
              <Text style={styles.linkValue} numberOfLines={1}>
                {shareUrl}
              </Text>
            </View>
          </View>

          <View style={styles.shareActions}>
            <Pressable
              onPress={handleCopyLink}
              style={styles.shareButton}
              accessibilityLabel="Copy Link"
              accessibilityRole="button"
            >
              <Copy color={palette.forest} size={15} strokeWidth={2} />
              <Text style={styles.shareButtonLabel}>{copied ? "Copied!" : "Copy Link"}</Text>
            </Pressable>
            <Pressable
              onPress={handleShare}
              style={styles.shareButton}
              accessibilityLabel="Share"
              accessibilityRole="button"
            >
              <ExternalLink color={palette.cedar} size={15} strokeWidth={2} />
              <Text style={styles.shareButtonLabel}>Share</Text>
            </Pressable>
          </View>
        </Panel>
      </Reveal>

      {/* Memorial details (read-only) */}
      <Reveal delay={130} scale>
        <Panel style={styles.detailSection}>
          <View style={styles.detailSectionHeader}>
            <Text style={styles.detailSectionTitle}>{memorial.lovedOneName}</Text>
          </View>

          {memorial.obituary ? (
            <View style={styles.detailField}>
              <Text style={styles.detailLabel}>Obituary</Text>
              <View style={styles.detailValueRow}>
                <Text style={styles.detailValue}>{memorial.obituary}</Text>
              </View>
            </View>
          ) : null}

          {memorial.serviceDetails ? (
            <View style={[styles.detailField, { marginTop: 16 }]}>
              <Text style={styles.detailLabel}>Service Details</Text>
              <View style={styles.detailValueRow}>
                <Text style={styles.detailValue}>{memorial.serviceDetails}</Text>
              </View>
            </View>
          ) : null}

          <View style={[styles.detailField, { marginTop: 16 }]}>
            <View style={styles.changeRequestRow}>
              <MessageCircle color={palette.inkSoft} size={16} strokeWidth={1.8} />
              <Text style={styles.changeRequestText}>
                To request a change to the memorial, please use the Messages tab
                to contact your care team.
              </Text>
            </View>
          </View>
        </Panel>
      </Reveal>

      {/* Event timeline */}
      {events.length > 0 ? (
        <>
          <Reveal delay={180}>
            <View style={styles.sectionHeader}>
              <Calendar color={palette.forest} size={18} strokeWidth={1.8} />
              <Text style={styles.sectionTitle}>Upcoming Events</Text>
            </View>
          </Reveal>

          {events.map((event, i) => (
            <Reveal key={event.id} delay={210 + i * 70}>
              <Panel style={styles.eventCard}>
                <View style={styles.eventCardHeader}>
                  <Text style={styles.eventTitle}>{event.title}</Text>
                  {event.livestreamUrl ? (
                    <Badge label="Livestream available" tone="forest" />
                  ) : null}
                </View>
                {event.startAt ? (
                  <Text style={styles.eventDate}>{formatLongDate(event.startAt)}</Text>
                ) : null}
                {event.startAt && event.endAt ? (
                  <Text style={styles.eventTime}>{formatTimeRange(event.startAt, event.endAt)}</Text>
                ) : null}
                {event.venue ? (
                  <Text style={styles.eventVenue}>{event.venue}</Text>
                ) : null}
                {event.address ? (
                  <Text style={styles.eventAddress}>{event.address}</Text>
                ) : null}
              </Panel>
            </Reveal>
          ))}
        </>
      ) : null}

      {/* Guest privacy note */}
      <Reveal delay={260 + events.length * 70}>
        <Panel style={styles.privacyNote}>
          <View style={styles.privacyRow}>
            <Users color={palette.inkSoft} size={16} strokeWidth={1.8} />
            <Text style={styles.privacyText}>
              {memorial.rsvpEnabled ? (
                <Text style={styles.privacyBold}>RSVPs are open. </Text>
              ) : (
                <Text style={styles.privacyBold}>RSVPs are closed. </Text>
              )}
              Guest details are private and managed by the funeral home to
              protect everyone's privacy.
            </Text>
          </View>
        </Panel>
      </Reveal>
    </View>
  );
}

const styles = StyleSheet.create({
  content: {
    gap: spacing.lg,
  },
  placeholderPanel: {
    gap: 8,
  },
  statRow: {
    flexDirection: "row",
    gap: 12,
    flexWrap: "wrap",
  },
  sharePanel: {
    gap: 14,
  },
  sharePanelHeader: {
    flexDirection: "row",
    gap: 8,
    alignItems: "center",
  },
  sharePanelTitle: {
    color: palette.ink,
    fontFamily: "Manrope_700Bold",
    fontSize: 15,
  },
  linkRow: {
    gap: 6,
  },
  linkBox: {
    backgroundColor: palette.canvasAlt,
    borderRadius: radii.sm,
    padding: 12,
    gap: 4,
  },
  linkLabel: {
    color: palette.inkSoft,
    fontFamily: "Manrope_600SemiBold",
    fontSize: 11,
    letterSpacing: 0.5,
    textTransform: "uppercase",
  },
  linkValue: {
    color: palette.forest,
    fontFamily: "Manrope_600SemiBold",
    fontSize: 14,
  },
  shareActions: {
    flexDirection: "row",
    gap: 10,
  },
  shareButton: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    backgroundColor: palette.white,
    borderRadius: radii.pill,
    borderWidth: 1,
    borderColor: palette.line,
    paddingVertical: 8,
    paddingHorizontal: 14,
  },
  shareButtonLabel: {
    color: palette.ink,
    fontFamily: "Manrope_600SemiBold",
    fontSize: 13,
  },
  detailSection: {
    gap: 4,
  },
  detailSectionHeader: {
    flexDirection: "row",
    gap: 8,
    alignItems: "center",
    marginBottom: 10,
  },
  detailSectionTitle: {
    color: palette.ink,
    fontFamily: "Manrope_700Bold",
    fontSize: 15,
  },
  detailField: {
    gap: 6,
  },
  detailLabel: {
    color: palette.inkSoft,
    fontFamily: "Manrope_600SemiBold",
    fontSize: 12,
    letterSpacing: 0.5,
    textTransform: "uppercase",
  },
  detailValueRow: {
    backgroundColor: palette.canvasAlt,
    borderRadius: radii.sm,
    padding: 12,
  },
  detailValue: {
    color: palette.ink,
    fontFamily: "Manrope_400Regular",
    fontSize: 14,
    lineHeight: 22,
  },
  changeRequestRow: {
    flexDirection: "row",
    gap: 8,
    alignItems: "flex-start",
  },
  changeRequestText: {
    flex: 1,
    color: palette.inkSoft,
    fontFamily: "Manrope_400Regular",
    fontSize: 13,
    lineHeight: 20,
  },
  sectionHeader: {
    flexDirection: "row",
    gap: 8,
    alignItems: "center",
  },
  sectionTitle: {
    color: palette.ink,
    fontFamily: "Manrope_700Bold",
    fontSize: 16,
  },
  eventCard: {
    gap: 4,
  },
  eventCardHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: 8,
    marginBottom: 4,
  },
  eventTitle: {
    flex: 1,
    color: palette.ink,
    fontFamily: "Manrope_700Bold",
    fontSize: 16,
    lineHeight: 22,
  },
  eventDate: {
    color: palette.forest,
    fontFamily: "Manrope_600SemiBold",
    fontSize: 13,
    lineHeight: 20,
  },
  eventTime: {
    color: palette.inkSoft,
    fontFamily: "Manrope_400Regular",
    fontSize: 13,
    lineHeight: 20,
  },
  eventVenue: {
    color: palette.ink,
    fontFamily: "Manrope_500Medium",
    fontSize: 13,
    lineHeight: 20,
    marginTop: 4,
  },
  eventAddress: {
    color: palette.inkSoft,
    fontFamily: "Manrope_400Regular",
    fontSize: 12,
    lineHeight: 18,
  },
  privacyNote: {
    backgroundColor: "rgba(251, 248, 241, 0.7)",
  },
  privacyRow: {
    flexDirection: "row",
    gap: 10,
    alignItems: "flex-start",
  },
  privacyText: {
    flex: 1,
    color: palette.inkSoft,
    fontFamily: "Manrope_400Regular",
    fontSize: 13,
    lineHeight: 21,
  },
  privacyBold: {
    fontFamily: "Manrope_600SemiBold",
    color: palette.ink,
  },
});
